import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, Search, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";
import { cn } from "@/lib/utils";
import { reasonText, serverMessage } from "./ReferCopy";

/** Is this term a half-typed Employee ID, a finished one, or a name?
 *
 *  An ID here is letters then digits — SHPL1234 — and the shapes come from the records
 *  themselves, so nothing is hard-coded. While a code is half typed there is nothing worth
 *  searching for: `SHPL` alone would list strangers, and `SHPL123` lists four of them. A name is
 *  anything that is not going to become a code, and it searches from three letters.
 */
export const termKind = (term, prefixes = []) => {
  const up = term.trim().toUpperCase();
  if (!up) return "short";
  for (const { prefix, digits } of prefixes) {
    if (prefix.startsWith(up)) return "waiting";                       // S, SH, SHP, SHPL
    if (up.startsWith(prefix)) {
      const rest = up.slice(prefix.length);
      if (/^\d*$/.test(rest)) return rest.length >= digits ? "code" : "waiting";
    }
  }
  return up.length >= 3 ? "name" : "short";
};

/** The gate. Find yourself, then type out the whole mobile number already on your record — the
 *  page only ever shows its last two digits, so it proves who you are without giving it away.
 *  Somebody who may not refer is told why and gets no further. */
export const ReferVerify = ({ t, config, onVerified, onHelpdesk }) => {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState(null);
  const [searching, setSearching] = useState(false);
  const [who, setWho] = useState(null);        // the eligibility answer for the picked person
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const term = q.trim();
  const kind = termKind(term, config?.code_prefixes);
  const waiting = kind === "waiting";

  useEffect(() => {
    if (kind !== "code" && kind !== "name") { setMatches(null); return undefined; }
    setSearching(true);
    const timer = setTimeout(() => {
      axios.get(`${API_BASE}/public/refer/search`, { params: { q: term } })
        .then(({ data }) => setMatches(data.matches || []))
        .catch(() => setMatches([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [term, kind]);

  const pick = async (empId) => {
    setError(null); setPhone("");
    try {
      const { data } = await axios.get(`${API_BASE}/public/refer/check`, {
        params: { emp_id: empId },
      });
      setWho(data);
    } catch (e) {
      setError(serverMessage(e?.response?.data?.detail, t));
    }
  };

  // A finished code that matches nobody is still asked about by name — somebody deleted from the
  // records is the only way to get here, and they deserve the reason rather than "nobody found".
  useEffect(() => {
    if (matches?.length === 0 && kind === "code") pick(term.toUpperCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, term, kind]);

  const verify = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API_BASE}/public/refer/verify`, {
        emp_id: who.emp_id, phone,
      });
      onVerified({ ...data, phone });
    } catch (e) {
      setError(serverMessage(e?.response?.data?.detail, t));
    } finally { setBusy(false); }
  };

  const blocked = who && who.reason_code;

  return (
    <section className="rounded-2xl border bg-card p-5" data-testid="refer-verify">
      <h2 className="flex items-center gap-2 text-base font-bold md:text-lg">
        <ShieldCheck className="h-4 w-4 text-emerald-600" />
        {who && !blocked ? t.phoneTitle : t.verifyTitle}
      </h2>

      {!who && (
        <>
          <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{t.verifyLead}</p>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t.searchPlaceholder} autoComplete="off"
              className="h-12 pl-9 text-[15px]" data-testid="refer-search"
            />
          </div>
          {waiting && (
            <p className="mt-2 text-[12px] text-muted-foreground" data-testid="refer-code-hint">
              {t.codeHint}
            </p>
          )}
          {searching && (
            <p className="mt-2 flex items-center gap-2 text-[12px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.searching}
            </p>
          )}
          {matches?.length > 0 && (
            <>
              <p className="mt-3 text-[12px] font-medium text-muted-foreground">{t.pickYou}</p>
              <div className="mt-1.5 space-y-1.5" data-testid="refer-matches">
                {matches.map((m) => (
                  <button
                    key={m.emp_id} type="button" onClick={() => pick(m.emp_id)}
                    data-testid={`refer-match-${m.emp_id}`}
                    className="flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-3 text-left transition-colors hover:border-emerald-500/60 hover:bg-emerald-500/5 active:bg-emerald-500/10"
                  >
                    <span className="min-w-0">
                      <span className="block text-[14px] font-semibold">{m.emp_id} · {m.name}</span>
                      <span className="block truncate text-[12px] text-muted-foreground">
                        {m.role} · {m.branch}
                      </span>
                    </span>
                    {!m.can_refer && (
                      <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400"
                        data-testid={`refer-match-blocked-${m.emp_id}`}>
                        {t.cannotRefer}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
          {matches?.length === 0 && !searching && kind !== "code" && (
            <p className="mt-3 text-[13px] text-muted-foreground" data-testid="refer-no-match">
              {t.noMatch}
            </p>
          )}
        </>
      )}

      {who && (
        <div className="mt-3">
          <div className="flex items-center justify-between gap-3 rounded-xl border bg-muted/40 px-3 py-2.5">
            <div className="min-w-0">
              <p className="truncate text-[14px] font-semibold" data-testid="refer-me-name">
                {who.emp_id} · {who.name}
              </p>
              <p className="truncate text-[12px] text-muted-foreground">{who.role} · {who.branch}</p>
            </div>
            <Button variant="ghost" size="sm" className="shrink-0 text-[12px]"
              onClick={() => { setWho(null); setQ(""); setMatches(null); setError(null); }}
              data-testid="refer-not-me">{t.notYou}</Button>
          </div>

          {blocked ? (
            <div className="mt-3 rounded-xl border border-amber-500/50 bg-amber-500/10 p-3.5"
              data-testid="refer-blocked">
              <p className="flex items-center gap-2 text-[14px] font-semibold text-amber-900 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 shrink-0" /> {t.blockedTitle}
              </p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-amber-900/85 dark:text-amber-300/85">
                {reasonText(who, t)}
              </p>
              <Button variant="outline" size="sm" className="mt-3 h-9 text-[12px]"
                onClick={() => onHelpdesk(who)} data-testid="refer-blocked-helpdesk">
                {t.helpdesk}
              </Button>
            </div>
          ) : (
            <>
              <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
                {t.phoneLead.replace("{tail}", `••••••••${who.phone_tail}`)}
              </p>
              <Input
                value={phone} inputMode="numeric" maxLength={10} autoComplete="off"
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder={t.phoneHint} className="mt-2 h-12 text-[16px] tracking-[0.18em]"
                data-testid="refer-verify-phone"
              />
              <Button
                className="mt-3 h-12 w-full bg-emerald-600 text-[15px] font-semibold text-white hover:bg-emerald-700"
                disabled={phone.length !== 10 || busy} onClick={verify}
                data-testid="refer-verify-submit"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t.verifyBtn}
              </Button>
              <button type="button" onClick={() => onHelpdesk(who)} data-testid="refer-help-nudge"
                className="mt-3 w-full text-[12px] text-muted-foreground underline underline-offset-2">
                {t.helpNudge}
              </button>
            </>
          )}
        </div>
      )}

      {error && (
        <p className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive"
          data-testid="refer-verify-error">{error}</p>
      )}
    </section>
  );
};
